import React, { useState, useMemo } from "react";
import { ModalCompAdvance } from "../../../../components/Ui/ModalComp";
import { ButtonComp } from "../../../../components/Ui/ButtonComp";
import { SelectComp } from "../../../../components/Ui/selectComp";
import { TextAreaComp } from "../../../../components/Ui/TextInputComp";
import { formatMoney } from "../../../../utils/currencyFormat";
import { BsInfoCircleFill, BsExclamationTriangleFill } from "react-icons/bs";
import { useUpdateAttendanceStatusMutation } from "../../../../store/admin/attendanceSlice";
import { toast } from "react-hot-toast";

const AttendanceStatusChangeModal = ({ 
  show, 
  handleClose, 
  memberData = { id: null, name: "John Doe", member_no: "MC-2025-001" },
  currentStatus = { id: 4, name: "Non-Approved Absent", entry_id: null },
  fineData = { amount: 1000, status: "PAID" },
}) => {
  const [newStatusId, setNewStatusId] = useState("");
  const [reason, setReason] = useState("");
  const [updateStatus, { isLoading }] = useUpdateAttendanceStatusMutation();

  const attendanceStatuses = [
    { id: 1, name: "Present and Early" },
    { id: 2, name: "Present but Late" },
    { id: 3, name: "Approved Absent" },
    { id: 4, name: "Non-Approved Absent" },
  ];

  const financialImpact = useMemo(() => {
    if (!newStatusId) return null;

    const newId = parseInt(newStatusId);
    const oldId = currentStatus.id;

    const isChangingToPresent = (newId === 1 || newId === 2 || newId === 3);
    const isCurrentlyAbsent = oldId === 4;

    if (isCurrentlyAbsent && isChangingToPresent) {
      if (fineData.status === "PAID") {
        return {
          type: "success",
          message: `Changing this member to ${attendanceStatuses.find(s => s.id === newId)?.name} will reverse the paid fine of ${formatMoney(fineData.amount)}. The amount will be automatically credited to the member's wallet.`,
          icon: <BsInfoCircleFill className="text-success me-2" size={20} />
        };
      } else {
        return {
          type: "warning",
          message: `Changing this member to ${attendanceStatuses.find(s => s.id === newId)?.name} will waive the unpaid fine of ${formatMoney(fineData.amount)}.`,
          icon: <BsInfoCircleFill className="text-warning me-2" size={20} />
        };
      }
    }

    if (!isCurrentlyAbsent && newId === 4) {
      return {
        type: "danger",
        message: `Changing this member to Non-Approved Absent will apply a new fine of ${formatMoney(fineData.amount || 1000)}. The member will be notified.`,
        icon: <BsExclamationTriangleFill className="text-danger me-2" size={20} />
      };
    }

    return {
      type: "neutral",
      message: "This change has no direct financial impact.",
      icon: <BsInfoCircleFill className="text-secondary me-2" size={20} />
    };
  }, [newStatusId, currentStatus.id, fineData]);

  const handleSubmit = async () => {
    if (!newStatusId) return;

    try {
      const result = await updateStatus({
        member_id: memberData.id,
        entry_id: currentStatus.entry_id,
        new_status_id: parseInt(newStatusId),
        reason
      }).unwrap();

      if (result.status) {
        toast.success(result.message || "Attendance status updated successfully");
        handleClose();
      } else {
        toast.error(result.message || "Failed to update attendance status");
      }
    } catch (error) {
      toast.error(error?.data?.message || "An error occurred while updating status");
    }
  };

  const modalBody = (
    <div className="py-2">
      <div className="mb-4">
        <h5 className="fw-3 mb-1">Edit Attendance Status</h5>
        <p className="text-muted small">Update attendance for {memberData.name} ({memberData.member_no})</p>
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div className="p-3 bg-light rounded-3 mb-3 border">
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted fw-2">Current Status:</span>
              <span className={`px-3 py-1 rounded-pill text-white small`} style={{ 
                backgroundColor: currentStatus.id === 4 ? "#FF4343" : "#19BC31" 
              }}>
                {currentStatus.name}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12">
          <SelectComp
            labelContext="New Attendance Status"
            labelText="Select New Status"
            required={true}
            value={newStatusId}
            onChange={(e) => setNewStatusId(e.target.value)}
            arrayComp={attendanceStatuses.filter(s => s.id !== currentStatus.id)}
            input="name"
            inputValue="id"
            selectText="Choose Status"
          />
        </div>

        {financialImpact && (
          <div className="col-12">
            <div className={`p-3 rounded-3 d-flex align-items-start`} style={{ 
              backgroundColor: financialImpact.type === 'success' ? '#dafceb' : 
                               financialImpact.type === 'warning' ? '#fcefe2' : 
                               financialImpact.type === 'danger' ? '#e0a8ae' : '#f1f1f1',
              border: `1px solid ${financialImpact.type === 'success' ? '#20b16a' : 
                                     financialImpact.type === 'warning' ? '#ff9500' : 
                                     financialImpact.type === 'danger' ? '#d40000' : '#e0e0e0'}`
            }}>
              {financialImpact.icon}
              <p className="mb-0 small fw-2" style={{ lineHeight: '1.5' }}>
                {financialImpact.message}
              </p>
            </div>
          </div>
        )}

        <div className="col-12 mt-3">
          <TextAreaComp
            LabelText="Reason for Change (Optional)"
            placeholder="e.g. Member arrived late but was mistakenly marked absent"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="mt-4 d-flex gap-3">
        <ButtonComp
          btnText="Cancel"
          btnClassName="w-100 py-3 rounded-3 bg-white border text-dark"
          onClick={handleClose}
        />
        <ButtonComp
          btnText="Confirm Change"
          btnClassName="w-100 py-3 rounded-3 text-white"
          style={{ backgroundColor: "var(--color-primary)" }}
          disabled={!newStatusId || isLoading}
          loading={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );

  return (
    <ModalCompAdvance
      show={show}
      handleClose={handleClose}
      close={true}
      centered={true}
      size="md"
      arrayComp={[{ component: modalBody }]}
      modalBody={0}
    />
  );
};

export default AttendanceStatusChangeModal;

